import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Input, UncontrolledTooltip,
} from "reactstrap";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── ID de la catégorie "Voix" (Téléphonie Fixe) ── */
const VOIX_CATEGORY_ID = 9;

/* ── interfaces ── */
interface ServiceItem {
    contrat: { ref: string };
    service: {
        label: string;
        ref?: string;
        ref_ext?: string;
        description?: string;
        statut_service: string;
        date_ouverture?: string;
        date_ouverture_prevue?: string;
        qty?: number | string;
        categorie_id?: number | string;
        categorie_label?: string;
        extrafields?: Record<string, any>;
    };
}

/* ── helpers ── */
const formatDate = (raw?: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/* Normalisation : la catégorie "voix" peut arriver sous différents libellés */
const isVoixCategory = (s: ServiceItem["service"]) => {
    if (s.categorie_id !== undefined && s.categorie_id !== null) {
        return Number(s.categorie_id) === VOIX_CATEGORY_ID;
    }
    const lbl = (s.categorie_label || "").toLowerCase().trim();
    return lbl === "voix" || lbl.includes("téléphonie fixe") || lbl.includes("telephonie fixe");
};

/* Récupère le numéro SDA / fixe à afficher */
const getSda = (s: ServiceItem["service"]): string | null => {
    const ef = s.extrafields || {};
    const key = Object.keys(ef).find((k) => {
        const lk = k.toLowerCase();
        return lk.includes("sda") || lk.includes("numero") || lk.includes("numéro") || lk === "tel" || lk === "phone";
    });
    if (key && ef[key]) return String(ef[key]);
    return null;
};

/* Formate un numéro fixe FR : 01 23 45 67 89 */
const formatPhoneFr = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
    }
    return raw;
};

/* ── CDR helpers ── */
interface CdrGroup {
    destination_label: string;
    nb_calls: number;
    total_billable_duration?: number;
    total_technical_duration: number; // secondes — durée technique (réelle)
}

/* Normalise un numéro vers le format `calling_identity_number` stocké en base
   au format international E.164 avec préfixe `+` (ex: +33186221601, +351212345678). */
const normalizeSda = (raw?: string | null): string | null => {
    if (!raw) return null;
    let d = String(raw).replace(/\D/g, "");
    if (!d) return null;
    if (d.startsWith("00")) d = d.slice(2);
    /* 10 chiffres commençant par 0 = numéro national français → préfixer 33 */
    if (d.length === 10 && d.startsWith("0")) d = "33" + d.slice(1);
    return "+" + d;
};

/* Période = mois en cours (du 1er au jour courant inclus) */
const currentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${d}` };
};

/* Formatte une durée en secondes -> "HH:MM:SS" (ex 969 -> "00:16:09") */
const formatDuration = (seconds: number): string => {
    const s = Math.max(0, Math.round(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/* Type d'agrégation à afficher selon la destination */
type CdrUnit = "duration" | "sms" | "mms";
const getCdrUnit = (label?: string): CdrUnit => {
    const l = (label || "").toUpperCase();
    if (l.startsWith("SMS")) return "sms";
    if (l.startsWith("MMS")) return "mms";
    return "duration";
};

/* Valeur formatée affichée pour un groupe selon son unité */
const formatCdrGroupValue = (g: { destination_label: string; nb_calls: number; total_technical_duration: number; }): string => {
    const unit = getCdrUnit(g.destination_label);
    if (unit === "sms") return `${g.nb_calls} SMS`;
    if (unit === "mms") return `${g.nb_calls} MMS`;
    return formatDuration(g.total_technical_duration);
};

/* ── composant ── */
const TelephonieFixe = () => {
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    /* CDR du mois en cours, indexé par SDA normalisé */
    const [cdrBySda, setCdrBySda] = useState<Record<string, CdrGroup[]>>({});

    useEffect(() => {
        (async () => {
            try {
                const cfg = apiV2PostConfig();
                const res: any = await axios.post("/apiV2/", { action: "services" }, cfg);
                const all: ServiceItem[] = res?.data ?? res ?? [];
                /* On ne garde que la téléphonie fixe (catégorie 9) */
                const filteredVoix = all.filter((x) => isVoixCategory(x.service));
                setServices(filteredVoix);
                setLoading(false);

                /* fetch CDR du mois en cours en 1 seul appel batch */
                const sdas = Array.from(
                    new Set(
                        filteredVoix
                            .map((x) => normalizeSda(getSda(x.service)))
                            .filter((s): s is string => !!s)
                    )
                );
                if (sdas.length > 0) {
                    const { from, to } = currentMonthRange();
                    try {
                        const cdrRes: any = await axios.post(
                            "/apiV2/",
                            {
                                action: "cdr",
                                subaction: "batchgroupby",
                                sdas,
                                from,
                                to,
                                exclude: "Data",
                            },
                            cfg
                        );
                        const cdrData: Record<string, CdrGroup[]> = cdrRes?.data?.data ?? cdrRes?.data ?? {};
                        setCdrBySda(cdrData);
                    } catch {
                        /* CDR indisponibles, on laisse les colonnes vides */
                    }
                }
            } catch {
                setLoading(false);
            }
        })();
    }, []);

    /* ── KPI ── */
    const total = services.length;
    const actifs = services.filter((x) => x.service.statut_service === "Actif").length;
    const inactifs = services.filter((x) => x.service.statut_service === "Inactif").length;
    const autres = total - actifs - inactifs;

    const widgets = [
        { label: "Total lignes", counter: total, icon: "ri-phone-line", color: "primary" },
        { label: "Lignes actives", counter: actifs, icon: "ri-checkbox-circle-line", color: "success" },
        { label: "Lignes inactives", counter: inactifs, icon: "ri-close-circle-line", color: "danger" },
        { label: "En cours", counter: autres, icon: "ri-time-line", color: "warning" },
    ];

    /* ── filtre recherche ── */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return services;
        return services.filter((x) => {
            const efValues = Object.values(x.service.extrafields || {})
                .map((v) => String(v ?? "").toLowerCase())
                .join(" ");
            return (
                x.contrat.ref.toLowerCase().includes(q) ||
                x.service.label.toLowerCase().includes(q) ||
                (x.service.ref_ext || "").toLowerCase().includes(q) ||
                efValues.includes(q)
            );
        });
    }, [services, search]);

    /* ── loading ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de votre téléphonie fixe…</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Téléphonie Fixe" pageTitle="Espace Client" />

                    {/* ── Widgets KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Tableau ── */}
                    <Row>
                        <Col lg={12}>
                            <Card id="voixList">
                                <CardHeader className="border-0">
                                    <div className="d-flex align-items-center">
                                        <h5 className="card-title mb-0 flex-grow-1">Liste des lignes fixes</h5>
                                        <div className="flex-shrink-0">
                                            <div className="search-box">
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Rechercher une ligne, contrat, SDA…"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    style={{ minWidth: 260 }}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {filtered.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                    <i className="ri-phone-line"></i>
                                                </span>
                                            </div>
                                            <h5 className="mt-2">Aucune ligne fixe trouvée</h5>
                                            <p className="text-muted">
                                                {search
                                                    ? "Aucun résultat pour cette recherche."
                                                    : "Vous n'avez pas encore de ligne de téléphonie fixe active."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive table-card mb-1">
                                            <table className="table align-middle table-nowrap mb-0">
                                                <thead className="table-light text-muted">
                                                    <tr>
                                                        <th>Contrat</th>
                                                        <th>Ligne</th>
                                                        <th>Numéro SDA</th>
                                                        <th>Conso voix (mois)</th>
                                                        <th>Date ouverture</th>
                                                        <th className="text-center">Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map((item, idx) => {
                                                        const isActif = item.service.statut_service === "Actif";
                                                        const date = isActif
                                                            ? item.service.date_ouverture
                                                            : item.service.date_ouverture_prevue;
                                                        const sda = getSda(item.service);
                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <span className="fw-semibold link-primary">
                                                                        {item.contrat.ref}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="fw-medium">
                                                                        {item.service.label}
                                                                        {Number(item.service.qty ?? 1) > 1 && (
                                                                            <span className="text-muted ms-1">
                                                                                ({Number(item.service.qty)})
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {item.service.ref_ext && (
                                                                        <div className="text-muted fs-12">
                                                                            Réf. : {item.service.ref_ext}
                                                                        </div>
                                                                    )}
                                                                    {item.service.description && (
                                                                        <div
                                                                            className="text-muted fs-12 text-truncate"
                                                                            style={{ maxWidth: 360 }}
                                                                            dangerouslySetInnerHTML={{ __html: item.service.description }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {sda ? (
                                                                        <span className="badge bg-success-subtle text-success fs-12">
                                                                            <i className="ri-phone-fill me-1"></i>
                                                                            {formatPhoneFr(sda)}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted">—</span>
                                                                    )}
                                                                </td>
                                                                <td style={{ minWidth: 180 }}>
                                                                    {(() => {
                                                                        const sdaNorm = normalizeSda(sda);
                                                                        if (!sdaNorm) return <span className="text-muted fs-12">—</span>;
                                                                        const groups = cdrBySda[sdaNorm];
                                                                        if (!groups || groups.length === 0) {
                                                                            return <span className="text-muted fs-12">Aucun appel</span>;
                                                                        }
                                                                        /* Voix : somme des durées (hors SMS/MMS) */
                                                                        const totalSec = groups
                                                                            .filter((g) => getCdrUnit(g.destination_label) === "duration")
                                                                            .reduce((sum, g) => sum + (g.total_technical_duration || 0), 0);
                                                                        /* SMS / MMS : somme des nb_calls */
                                                                        const totalSms = groups
                                                                            .filter((g) => getCdrUnit(g.destination_label) === "sms")
                                                                            .reduce((sum, g) => sum + (g.nb_calls || 0), 0);
                                                                        const totalMms = groups
                                                                            .filter((g) => getCdrUnit(g.destination_label) === "mms")
                                                                            .reduce((sum, g) => sum + (g.nb_calls || 0), 0);
                                                                        const tipId = `cdr-tip-fixe-${idx}`;
                                                                        return (
                                                                            <>
                                                                                <div id={tipId}>
                                                                                    {totalSec > 0 && (
                                                                                        <div className="d-flex align-items-center">
                                                                                            <i className="ri-time-line text-primary me-1"></i>
                                                                                            <span className="fw-semibold fs-13">{formatDuration(totalSec)}</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {(totalSms > 0 || totalMms > 0) && (
                                                                                        <div className="d-flex align-items-center text-muted fs-12">
                                                                                            <i className="ri-message-2-line me-1"></i>
                                                                                            {totalSms > 0 && <span className="me-2">{totalSms} SMS</span>}
                                                                                            {totalMms > 0 && <span>{totalMms} MMS</span>}
                                                                                        </div>
                                                                                    )}
                                                                                    {totalSec === 0 && totalSms === 0 && totalMms === 0 && (
                                                                                        <span className="text-muted fs-12">Aucun appel</span>
                                                                                    )}
                                                                                </div>
                                                                                <UncontrolledTooltip placement="left" target={tipId} autohide={false}>
                                                                                    <div className="text-start">
                                                                                        <div className="fw-semibold mb-1">Détail par destination</div>
                                                                                        {groups.map((g, j) => (
                                                                                            <div key={j} className="d-flex justify-content-between gap-3">
                                                                                                <span>{g.destination_label || "—"}</span>
                                                                                                <span className="fw-semibold">{formatCdrGroupValue(g)}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </UncontrolledTooltip>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td className="text-muted">{formatDate(date)}</td>
                                                                <td className="text-center">
                                                                    {isActif ? (
                                                                        <Badge className="bg-success-subtle text-success text-uppercase">
                                                                            <i className="ri-checkbox-circle-fill me-1"></i>Actif
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge className="bg-danger-subtle text-danger text-uppercase">
                                                                            <i className="ri-time-fill me-1"></i>
                                                                            {item.service.statut_service}
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default TelephonieFixe;
